
/**
 * Utilities for handling routing in the application
 */

// Get the base URL for the application
export const getBaseUrl = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  
  return window.location.origin;
};

// Navigate safely with history
export const navigateSafely = (path: string): void => {
  // If we're already on this path, don't navigate
  if (window.location.pathname === path) {
    console.log(`Already on path: ${path}`);
    return;
  }
  
  // Use history API if available
  if (window.history && window.history.pushState) {
    window.history.pushState({}, '', path);
    // Dispatch a popstate event to trigger route handlers
    const popStateEvent = new PopStateEvent('popstate', { state: {} });
    window.dispatchEvent(popStateEvent);
  } else {
    // Fallback to location change
    window.location.href = `${window.location.origin}${path}`;
  }
};

// Check if a route is valid in our application
export const isValidRoute = (path: string): boolean => {
  // List of valid routes in the application
  const validRoutes = [
    '/',
    '/about',
    '/contact',
    '/careers',
    '/blog',
    '/jobs',
    '/login',
    '/signup',
    '/employer-login',
    '/application',
    '/assessment',
    '/video-intro',
    '/application-status',
    '/dashboard',
    '/admin',
    '/terms',
    '/privacy',
    '/cookie-policy',
    '/faq',
    '/tips',
    '/enterprise',
    '/pricing'
  ];
  
  // Special case for admin routes with parameters
  if (path.startsWith('/admin/')) {
    return true;
  }
  
  // Check if the path starts with any valid route
  return validRoutes.some(route => 
    path === route || 
    path.startsWith(`${route}/`) || 
    path.startsWith(`${route}?`)
  );
};

// Get the absolute admin URL that works on any domain
export const getAdminUrl = (): string => {
  return '/admin';
};

// Detect deployment environment
export const getDeploymentInfo = (): { platform: string, environment: string, deployType: string } => {
  if (typeof window === 'undefined') {
    return { platform: 'unknown', environment: 'server', deployType: 'unknown' };
  }

  const hostname = window.location.hostname;
  
  // Environment detection logic
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isVercelPreview = hostname.includes('vercel.app') || hostname.includes('lovableproject.com');
  const isProdDomain = hostname === 'truehr.co' || hostname === 'www.truehr.co';
  
  console.log('Deployment detection:', {
    hostname,
    isLocalhost,
    isVercelPreview,
    isProdDomain
  });

  if (isLocalhost) {
    return { 
      platform: 'local', 
      environment: 'development',
      deployType: 'local' 
    };
  } else if (isVercelPreview) {
    return { 
      platform: 'vercel', 
      environment: 'preview', 
      deployType: 'vercel-preview'
    };
  } else if (isProdDomain) {
    return { 
      platform: 'vercel', 
      environment: 'production',
      deployType: 'production' 
    };
  } else {
    // For other deployments
    return { 
      platform: 'other', 
      environment: 'preview',
      deployType: 'preview' 
    };
  }
};

// Force refresh application to get the latest changes
export const forceRefresh = (): void => {
  // Clear cache and hard reload
  if (typeof window !== 'undefined') {
    console.log('Forcing application refresh to get latest changes');
    window.location.reload();
  }
};

// Check if we need to bust the cache
export const checkForUpdates = (version: string): boolean => {
  const lastVersion = localStorage.getItem('app_version');
  if (lastVersion !== version) {
    localStorage.setItem('app_version', version);
    console.log(`New version detected: ${version} (was: ${lastVersion})`);
    return true;
  }
  return false;
};

// Register cleanup handlers
export const registerCleanupHandlers = (): void => {
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      console.log('Cleaning up before page unload');
    });
    
    window.addEventListener('pagehide', () => {
      console.log('Page is being hidden, performing cleanup');
    });
  }
};

// Export these for debugging purposes
export const DEBUG_INFO = {
  currentPath: typeof window !== 'undefined' ? window.location.pathname : '',
  hostname: typeof window !== 'undefined' ? window.location.hostname : '',
  origin: typeof window !== 'undefined' ? window.location.origin : '',
  deploymentInfo: typeof window !== 'undefined' ? getDeploymentInfo() : { platform: 'unknown', environment: 'server', deployType: 'unknown' },
  validRoutes: [
    '/',
    '/about',
    '/contact',
    '/careers',
    '/blog',
    '/jobs',
    '/login',
    '/signup',
    '/employer-login',
    '/application',
    '/assessment',
    '/video-intro',
    '/application-status',
    '/dashboard',
    '/admin',
    '/terms',
    '/privacy',
    '/cookie-policy',
    '/faq',
    '/tips',
    '/enterprise',
    '/pricing'
  ]
};
